/**
 * @file コンポーネント：RavnurPlayer ロジック管理
 * ----------------------------------------------
 */

// ライブラリ読み込み
import React, { useEffect, useRef } from "react";
import { RavnurMediaPlayer } from "ravnur-player-public";

// 関数読み込み
import { fnIsIpad, fnIsIphone, fnIsSafari } from "~/src/functions/boolean";

// Presenter読み込み
import { Presenter } from "./presenter";

type PropsType = {
    fairplayCer: string;
    fairplayLaUrl: string;
    hasAudio: boolean;
    hideControl?: boolean;
    hidePlaceholder?: boolean;
    id?: number;
    mediaId: string;
    movieUrl: string;
    options?: {
        muted?: boolean; // DesktopPCの自動再生で必要
    };
    playreadyLaUrl: string;
    setMediaPlayer?: (playerEl: HTMLMediaElement) => void;
    widevineLaUrl: string;
    operationId: string;
};

// コンポーネントを返す
export const RavnurPlayer = (props: PropsType) => {
    const mounted = useRef(false);

    useEffect(() => {
        if (mounted.current) return;

        /*
         * プレイヤー生成
         */
        const element = document.querySelector(`#${props.mediaId} #player`);
        const player = new RavnurMediaPlayer(element);

        /*
         * メディア設定
         */
        const media = {
            src:
                props.movieUrl +
                (fnIsSafari()
                    ? "(format=m3u8-cmaf,encryption=cbcs)"
                    : "(format=mpd-time-cmaf,encryption=cenc)"),
            type: fnIsSafari()
                ? "application/x-mpegURL"
                : "application/dash+xml",
            ...(props.operationId && {
                preview: "/api/operations/thumbnails/vtt/video/" + props.operationId,
            }),
        };

        /*
         * オプション設定
         */
        // 共通
        const commonOptions = {
            ...(props.options || {}),
            playbackRates: [
                0.5,
                1,
                1.5,
                2,
                ...(!fnIsIpad() && !fnIsIphone() && !fnIsSafari()
                    ? [3, 4]
                    : []),
            ],
        };
        // DASH
        const dashOptions = {
            ...commonOptions,
            widevineURL: props.widevineLaUrl,
            playreadyURL: props.playreadyLaUrl,
        };
        // HLS
        const fairplayOptions = {
            ...commonOptions,
            fairplayURL: props.fairplayLaUrl,
            fairplayCertificateUrl: props.fairplayCer,
        };

        /*
         * メディアとオプションをセット
         */
        fnIsSafari()
            ? player.setup(media, fairplayOptions)
            : player.setup(media, dashOptions);

        mounted.current = true;

        // コンポーネントがアンマウントされた時にプレイヤーを破棄
        return () => {
            if (player) player.destroy();
        };
    }, [mounted]);

    // media playerのセット関数が渡されている場合は追加
    useEffect(() => {
        if (!props.setMediaPlayer) return;
        if (mounted.current) {
            const videoEl = document.querySelector(
                `#${props.mediaId} video`
            ) as HTMLMediaElement;

            if (videoEl) {
                let isSet = false;
                const handler = () => {
                    console.log("Video element ready handler!");
                    if (isSet) return; // 2回目以降は無視
                    console.log("Video element ready event fired!");
                    isSet = true;
                    props.setMediaPlayer(videoEl);
                };

                videoEl.addEventListener("loadedmetadata", handler);
                videoEl.addEventListener("loadeddata", handler);
                videoEl.addEventListener("canplay", handler);

                return () => {
                    videoEl.removeEventListener("loadedmetadata", handler);
                    videoEl.removeEventListener("loadeddata", handler);
                    videoEl.removeEventListener("canplay", handler);
                };
            }
        }
    }, [mounted]);

    // Presenterに渡す
    return (
        <Presenter
            hasAudio={props.hasAudio}
            mediaId={props.mediaId}
            hideControl={props.hideControl}
            hidePlaceholder={props.hidePlaceholder}
        />
    );
};
